import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { selectCurrentToken } from "../features/slices/authSlice";
import { useGetBankAccountsQuery } from "../features/api/userApiSlice";
import * as Constant from "../features/constant";

// Bank-registration gate. When REACT_APP_BANK_REGISTER === "true", a logged-in
// user with no bank account must be forced onto the add-bank page until they
// add one.
//
// Returns flags only — the consumer (App) enforces them at render time with
// <Navigate>, so no child route/component can navigate past the gate in a race,
// and game-launch routes can't slip through either.
const useBankRegistrationGate = () => {
  const token = useSelector(selectCurrentToken);
  const location = useLocation();

  const enabled = process.env.REACT_APP_BANK_REGISTER === "true";
  const addBankPath = Constant.getPath("/addbankaccount");

  const {
    data: bankAccountsData,
    isFetching,
    isError,
  } = useGetBankAccountsQuery(undefined, {
    skip: !enabled || !token,
    // Always pull a fresh list for the current session — the RTK Query cache
    // is not reset on logout, so without this a previous user's bank list
    // could leak in right after a fresh login and skip the gate.
    refetchOnMountOrArgChange: true,
  });

  const onAddBankPage = location.pathname.includes("addbankaccount");
  const hasBankAccount = (bankAccountsData?.result?.data?.length || 0) > 0;

  // We have this session's answer (data arrived). On request error we treat the
  // gate as "answered" but fail open — same behaviour as before this gate.
  const haveData = !isFetching && bankAccountsData !== undefined;
  const answered = haveData || isError;

  // Flag on + logged in, but the list hasn't come back yet. Used to HOLD the
  // game / payment launcher routes so a cold load straight to /gameLauncher
  // can't start the game before the gate decides.
  const gatePending = enabled && !!token && !answered;

  const mustAddBank = enabled && !!token && haveData && !hasBankAccount && !onAddBankPage;
  const shouldLeaveAddBankPage =
    enabled && !!token && haveData && hasBankAccount && onAddBankPage;

  return { mustAddBank, shouldLeaveAddBankPage, gatePending, addBankPath };
};

export default useBankRegistrationGate;
